import { useCallback, useEffect, useMemo, useState } from "react";
import { AppShell, MarqueeHeader } from "../layout";
import { DateSelector, type DateRange } from "../components/DateSelector";
import { useAuth } from "../auth";
import { useOperatorDay, useOperatorSideDuties, type GameView, type SideDuties } from "../hooks/useOperatorDay";
import { ExtraReportSheet } from "./op/ExtraReportSheet";
import { SideStudy } from "./op/SideStudy"; import { SidePlayer } from "./op/SidePlayer"; /* 13.10: учебная игра — «ты играешь» и отчёты друг о друге */
import { HallAdminDay } from "./op/HallAdminDay";
import { takeOpDay, takeOpTab } from "../utils/deeplink";
import { todayBusiness, toBusinessDate, fromBusinessDate, fmtDateApi, nowUtc5, parseDateApi } from "../utils/date";
import { TabSchedule, Person, Tea, Coin } from "./op/icons";
import { OpTabs, TABS, type TabId } from "./op/OpTabs"; import { UniversityTab } from "./op/UniversityTab"; /* 28.09: «Юниверситет» вместо Skills */
import { DashActions } from "./op/DashActions"; /* 04.09 (владелец): «я на месте / доп.работа / покупка» + свои покупки дня */
import { ScheduleTab } from "./op/ScheduleTab";
import { BountyTab } from "./op/BountyTab";
import { ReportsTab } from "./op/ReportsTab";
import { AnketaTab } from "./op/AnketaTab"; import { ExtraTab } from "./op/ExtraTab";
import { SalaryTab } from "./op/SalaryTab"; /* 02.09 (владелец): зарплата оператора */
import { GameReportModal } from "./op/GameReportModal";
import type { ModalGame } from "./op/reportModel";
import { isMirKvSource } from "../utils/mirkv";
import { api } from "../api/client";
import { loadMkDraft, saveMkDraft } from "./op/reportDraft";
import { PlayersBadge, TimePicker } from "../components/shared";
import { GameQrPanel } from "../components/GameQrPanel";
import css from "./OperatorScreen.module.css";

/**
 * B.9 — Operator shell + Dashboard «Игры». Orange role. Header (wordmark +
 * `Operator | {ник}` HUD badge + tab-dependent right slot) → 8-tab strip →
 * dashboard. Игры is wired to real assignments; B.10 (График) lands in the
 * «sched» tab; other tabs stay placeholders until B.11 (Отчёты).
 */
export function OperatorScreen() {
  const { user } = useAuth();
  const [tab, setTab] = useState<TabId>(() => (takeOpTab() === "sched" ? "sched" : "games"));   // 13.10: кнопка «Открыть график» из Telegram
  const [date, setDate] = useState<Date>(() => { const d = takeOpDay(); return d ? parseDateApi(d) : todayBusiness(); });   // 28.09: «Посмотреть» из TG

  useEffect(() => {
    document.documentElement.dataset.role = "op";
  }, []);

  const onDateChange = useCallback((v: Date | DateRange) => {
    if (v instanceof Date) setDate(toBusinessDate(v));
  }, []);

  const nick = user?.nickname || user?.name || "guest";
  const operatorName = user?.name || nick;
  const operatorId = user?.staffId;
  const { games, loading, error, draftPending, reload } = useOperatorDay(date, operatorId);
  const showDate = tab === "games" || tab === "reports";
  /* 02.09 (владелец): у «Зарплаты» тот же календарь, но в режиме периода —
     выбранный диапазон = период, за который показана ЗП. Бизнес-даты. */
  const [salRange, setSalRange] = useState<{ start: Date; end: Date } | null>(null);
  const onSalRange = useCallback((v: Date | DateRange) => {
    if (v instanceof Date || !v.start || !v.end) return;
    setSalRange({ start: toBusinessDate(v.start), end: toBusinessDate(v.end) });
  }, []);
  const onSalSetRange = useCallback((f: string, t: string) => {
    const next = { start: parseDateApi(f), end: parseDateApi(t) };
    setSalRange(next);
    window.DateSelector?.setRange(fromBusinessDate(next.start), fromBusinessDate(next.end));
  }, []);
  const salRangeLocal = useMemo<DateRange>(() => ({
    start: salRange ? fromBusinessDate(salRange.start) : null, end: salRange ? fromBusinessDate(salRange.end) : null,
  }), [salRange]);

  return (
    <AppShell
      /* 27.08 (владелец): шапка и табы закреплены намертво — скроллит .main, не страница */
      lockViewport
      header={
        <MarqueeHeader
          roleLabel="OPERATOR"
          nick={nick}
          dateSelector={tab === "salary"
            ? <DateSelector key="salary-range" mode="range" initialRange={salRangeLocal} onChange={onSalRange} />
            : showDate ? <DateSelector key="day" mode="day" initialDate={fromBusinessDate(date)} onChange={onDateChange} /> : undefined}
        />
      }
      topZone={<OpTabs tab={tab} onTab={setTab} />}
    >
      <div className={css.screen}>
        {tab === "games" ? (
          <Dashboard games={games} loading={loading} error={error} draftPending={draftPending} date={date} operatorId={operatorId ?? ""} operatorName={operatorName} onReload={reload} />
        ) : tab === "uni" ? (
          <UniversityTab />
        ) : tab === "sched" ? (
          <ScheduleTab operatorId={operatorId} />
        ) : tab === "reports" ? (
          <ReportsTab date={date} operatorId={operatorId} operatorName={operatorName} />
        ) : tab === "anketa" ? (
          <AnketaTab />
        ) : tab === "bounty" ? (
          <BountyTab />
        ) : tab === "extra" ? <ExtraTab date={date} operatorId={operatorId ?? null} /> : tab === "salary" ? <SalaryTab operatorId={operatorId ?? null} range={salRange ? { from: fmtDateApi(salRange.start), to: fmtDateApi(salRange.end) } : null} onSetRange={onSalSetRange} /> : (
          <div className={css.placeholder}>Вкладка «{TABS.find((t) => t.id === tab)?.label}» — в разработке.</div>
        )}
      </div>
    </AppShell>
  );
}


/* ── Dashboard «Игры» ────────────────────────────────────────────────────── */

function Dashboard({
  games, loading, error, draftPending, date, operatorId, operatorName, onReload,
}: {
  games: GameView[]; loading: boolean; error: string | null; date: Date;
  /** Смена назначена, но ещё не отправлена — иначе пустой день выглядит как «забыли» (11.09). */
  draftPending: boolean;
  operatorId: string; operatorName: string; onReload: () => void;
}) {
  const side = useOperatorSideDuties(date, operatorId || undefined);
  const [sidePick, setSidePick] = useState<{ kind: "extra"; id: number; init?: string } | { kind: "admin"; branch: string; init?: string } | null>(null);
  const [reportOf, setReportOf] = useState<SideDuties["extras"][number] | null>(null);
  const paid = side.extras.filter((e) => !e.study);
  const count = games.length;
  /* «Нет отчётов» — прошедшие игры без сданного отчёта (назначена/принята). */
  const debt = useMemo(
    () => games.filter((g) => g.status !== "reported" && g.status !== "finished" && isPast(g, date)),
    [games, date],
  );

  /* Форма отчёта. День — всегда mode "new". Бэклог — "edit" для сданных (с prefill),
     "new" для несданных. */
  const [modal, setModal] = useState<ModalGame | null>(null);
  /* возвращённый отчёт (26.08): форма открывается режимом fix с предзаполнением */
  const openDayReport = (g: GameView) => setModal({
    bookingId: g.bookingId, questName: g.questName, shortName: g.questShort, time: g.time,
    date: fmtDateApi(date), clientName: g.client, bookedPlayers: g.players, source: g.source,
    tea: g.tea, teaRoomId: g.teaRoom, mc: g.mc, branchId: g.branchId,
    operatorId, operatorName,
    mode: g.fixReportId ? "fix" : "new", reportId: g.fixReportId, prefill: g.fixPrefill,
  });

  const [qrBookingId, setQrBookingId] = useState<number | null>(null);

  return (
    <div className={css.main}>
      {/* 04.09 (владелец): приветствие убрано — строка «я на месте / доп.работа / покупка» + счётчик игр и свои покупки дня (op/DashActions) */}
      <DashActions date={date} count={count} operatorId={operatorId} onReload={onReload} onSideReload={side.reloadSide} />

      {debt.length > 0 ? (
        <>
          <div className={`${css.secHead} ${css.danger}`}>
            <span className={css.ico}><Person /></span>
            Нет отчётов <span className={css.cnt}>({debt.length})</span>
          </div>
          {debt.map((g) => (
            <div
              key={`debt-${g.bookingId}`}
              className={css.debtCard}
              role="button"
              tabIndex={0}
              onClick={() => openDayReport(g)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openDayReport(g); } }}
            >
              <span className={css.debtWhen}>{g.time}</span>
              <span className={css.debtQuest}>{g.questName}</span>
              <span className={css.debtCta}>Сдать ›</span>
            </div>
          ))}
        </>
      ) : null}

      {/* 28.09 (владелец): админ зала — день филиала: все игры, кто на них, какие МК ведёт,
          «Принять» и отчёт о смене (op/HallAdminDay). Над «Играми дня»: это не свои игры. */}
      {side.admin ? <HallAdminDay date={date} admin={side.admin} css={css}
        onReport={(init) => setSidePick({ kind: "admin", branch: side.admin!.branch, init })} /> : null}
      <div className={`${css.secHead} ${css.orange}`}>
        <span className={css.ico}><TabSchedule /></span>
        Игры дня <span className={css.cnt}>({count})</span>
      </div>

      {/* 27.09 (владелец: «учёбка отображается как оплачиваемая доп-работа») — своя жёлтая карточка */}
      <SideStudy items={side.extras.filter((e) => e.study)} css={css} onChanged={side.reloadSide} /><SidePlayer date={date} css={css} />
      {paid.length ? (
        <>
          <div className={`${css.secHead} ${css.dopHead}`}>
            <span className={css.ico}>🔧</span> Доп.работы <span className={css.cnt}>({paid.length})</span>
          </div>
          {paid.map((e) => (
            <div key={e.id} className={`${css.dopCard} ${e.accepted ? css.dopAcc : ""}`} role="button" title="Тап — запросить другое время: ОО подтвердит, до этого платим по плану"
              onClick={() => setSidePick({ kind: "extra", id: e.id, init: `${e.factFrom ?? e.from}–${e.factTo ?? e.to}` })}>
              <div className={css.dopHeadRow}>
                <span className={css.dopTime}>{e.from}–{e.to}</span>
                {/* 26.08 (владелец): оператор должен видеть, на какой филиал идти */}
                {e.branch ? <span className={css.dopBranch}>{e.branch.toUpperCase()}</span> : null}
                {e.accepted ? <span className={css.dopSeen}>видел ✓</span> : (
                  <button type="button" className={css.dopAcceptBtn}
                    onClick={(ev) => { ev.stopPropagation(); void api(`/api/extra-work/${e.id}/accept`, { method: "POST" }).then(side.reloadSide).catch(() => {}); }}>✓ Принять</button>
                )}
              </div>
              {e.desc ? <div className={css.dopDesc}>{e.desc}</div> : null}
              {/* 11.09 (владелец: «такси так и не появилось»): отчёт нужен и здесь — на дашборде
                  человек видит доп.работу, а кнопка была только во вкладке «Доп». */}
              <div className={css.dopHint}>
                изменить время — запросом в ОО ›
                {e.taxi > 0 ? <span className={css.dopTaxi}> · 🚕 ₽{e.taxi}</span> : null}
                {e.factFrom && e.factTo && !e.factOk ? <span className={css.dopWait}> · факт {e.factFrom}–{e.factTo} ждёт ОО</span> : null}
              </div>
              <button type="button" className={css.dopReportBtn}
                onClick={(ev) => { ev.stopPropagation(); setReportOf(e); }}>
                {e.reportedAt ? "Отчёт ✓" : "Отчитаться — такси, что делал ›"}
              </button>
            </div>
          ))}
        </>
      ) : null}
      <ExtraReportSheet open={!!reportOf}
        work={reportOf ? {
          id: reportOf.id, description: reportOf.desc, timeFrom: reportOf.from, timeTo: reportOf.to,
          taxi: reportOf.taxi, taxiPaidBy: reportOf.taxiPaidBy,
          factFrom: reportOf.factFrom, factTo: reportOf.factTo, reportText: reportOf.reportText,
        } : null}
        onClose={() => setReportOf(null)}
        onDone={() => { setReportOf(null); side.reloadSide(); }} />
      {sidePick ? (
        <TimePicker
          open mode="range" step={5}
          label={sidePick.kind === "admin" ? "Админ зала — был с и до" : "Доп — время от и до"}
          value={sidePick.init}
          onCommit={(v) => {
            const m = v.match(/^(\d{2}:\d{2})[–-](\d{2}:\d{2})$/);
            if (m) {
              const body = { time_from: m[1], time_to: m[2] };
              if (sidePick.kind === "extra") void api(`/api/extra-work/${sidePick.id}`, { method: "PUT", body }).then(side.reloadSide).catch(() => {});
              else void api("/api/hall-admin-reports", { method: "POST", body: { ...body, branch_id: sidePick.branch, date: fmtDateApi(date) } }).then(side.reloadSide).catch(() => {});
            }
            setSidePick(null);
          }}
          onCancel={() => setSidePick(null)}
        />
      ) : null}

      {error ? (
        <div className={css.empty}>Ошибка: {error}</div>
      ) : loading ? (
        <div className={css.empty}>Загрузка…</div>
      ) : games.length === 0 ? (
        <div className={css.empty}>
          {draftPending
            ? "Смену на этот день ещё готовят — игры появятся, когда её отправят."
            : "Нет игр на этот день"}
        </div>
      ) : (
        games.map((g) => (
          <GameCard
            key={g.bookingId}
            game={g}
            operatorId={operatorId}
            onMkSaved={onReload}
            canReport={g.status !== "reported" && g.status !== "finished" && isPast(g, date)}
            onReport={openDayReport}
            onQr={(bookingId) => setQrBookingId(bookingId)}
          />
        ))
      )}

      {modal ? (
        <GameReportModal
          game={modal}
          onClose={() => setModal(null)}
          onSuccess={() => { setModal(null); onReload(); }}
        />
      ) : null}
      {qrBookingId != null ? <GameQrPanel bookingId={qrBookingId} onClose={() => setQrBookingId(null)} /> : null}
    </div>
  );
}

/**
 * Прошедшая ли игра. Вчера-и-раньше — всегда прошедшая. Сегодня — прошедшая,
 * если дневной слот (час ≥ 6) и его время уже наступило по UTC+5. Ночные
 * слоты (час < 6) — это раннее утро следующего дня, сегодня их не считаем
 * прошедшими.
 */
function isPast(g: GameView, date: Date): boolean {
  const today = todayBusiness();
  if (date.getTime() < today.getTime()) return true;
  if (date.getTime() > today.getTime()) return false;
  const [h, m] = g.time.split(":").map(Number);
  if (h == null || h < 6) return false;
  const now = nowUtc5();
  const nowMin = now.getUTCHours() * 60 + now.getUTCMinutes();
  /* 06.09 (владелец, жалоба Даши: «стою на 23:00, не могу заполнять отчёт» в 22:49): отчёт открывается
     за 30 минут до начала — вейверы и игроков заполняют до игры, деньги — после. */
  return h * 60 + (m ?? 0) - 30 <= nowMin;
}

/** МК-исход в карточке (26.08, владелец): кнопки 5.0/✕ синхронны с отчётом —
 *  сдан → PUT mk-status в него; не сдан → mk-драфт, форма стартует с ним. */
function MkOutcome({ g, operatorId, onSaved }: { g: GameView; operatorId: string; onSaved: () => void }) {
  const [draft, setDraft] = useState(() => loadMkDraft(g.bookingId, operatorId));
  const val = g.reportId ? g.mkStatus : draft;
  const set = (next: string) => {
    const v = val === next && !g.reportId ? "" : next; /* повторный тап снимает; 10.10: у сданного — нет, отметка обязательна у всех */
    if (g.reportId) void api(`/api/game-reports/${g.reportId}/mk-status`, { method: "PUT", body: { mk_status: v } }).then(onSaved).catch(() => {});
    else { saveMkDraft(g.bookingId, operatorId, v); setDraft(v); }
  };
  return (
    <div className={css.mkRow} onClick={(e) => e.stopPropagation()}>
      <span className={css.mkLbl}>Мир Квестов</span>
      <button type="button" className={`${css.mkBtn} ${css.mkScore} ${val === "score" ? css.mkOn : ""}`} onClick={() => set("score")}>5.0</button>
      <button type="button" className={`${css.mkBtn} ${css.mkCancel} ${val === "cancel" ? css.mkOn : ""}`} onClick={() => set("cancel")}>✕</button>
    </div>
  );
}

function GameCard({ game: g, operatorId, onMkSaved, canReport, onReport, onQr }: { game: GameView; operatorId: string; onMkSaved: () => void; canReport?: boolean; onReport?: (g: GameView) => void; onQr?: (bookingId: number) => void }) {
  const [open, setOpen] = useState(false);
  const statusCls = g.status === "accepted" ? css.sAccepted : g.status === "reported" ? css.sReported : g.status === "finished" ? css.sFinished : css.sAssigned;
  const statusLabel = g.status === "accepted" ? "Принята ✓" : g.status === "reported" ? "Отчёт сдан" : g.status === "finished" ? "Завершена" : "Назначена";

  return (
    <article className={`${css.gameCard} ${statusCls} ${isMirKvSource(g.source) ? css.mirkv : ""}`} onClick={() => setOpen((o) => !o)}>
      <div className={css.gameHead}>
        <span className={css.gameWhen}>{g.time}</span>
        <span className={css.gameQuest}>{g.questName}{g.competition ? <span className={css.alarmSpin} title="Конкуренты!">🚨</span> : null}{g.cancelled ? <span className={css.cancelBadge} title={g.cancelReason || "Игра отменена"}>ОТМЕНА</span> : null}{g.branchId ? <span className={css.gameBranch}>{g.branchId.toUpperCase()}</span> : null}{g.specialVersion ? <span className={css.svBadge} title={g.specialVersion}>СПЕЦ</span> : null}</span>
        <button type="button" className={css.qrBtn} onClick={(e) => { e.stopPropagation(); onQr?.(g.bookingId); }}>QR</button>
        <span className={`${css.gameStatus} ${statusCls}`}>{statusLabel}</span>
      </div>
      <div className={css.gameMeta}>
        {/* 05.09 (владелец): стандартный бейдж игроков; кликабельность (грейд ≥3 → карточка игроков) решает сам PlayersBadge */}
        <PlayersBadge count={g.players} bookingId={g.bookingId} size="md" />
        {g.tea ? <span className={css.iconTea} title="Чайная"><Tea /></span> : null}
        {g.prepaid ? <span className={css.iconPay} title="Предоплата"><Coin />{g.prepaidAmount > 0 ? <b className={css.payAmt}>₽{g.prepaidAmount.toLocaleString("ru")}</b> : null}</span> : null}
      </div>
      {g.buddies.length > 0 ? (
        <div className={css.gameBuddies}>С тобой: {g.buddies.map((b, i) => (
          <span key={b}>{i > 0 ? ", " : ""}<b>{b}</b></span>
        ))}</div>
      ) : null}
      {isMirKvSource(g.source) ? <MkOutcome g={g} operatorId={operatorId} onSaved={onMkSaved} /> : null}
      {g.status === "assigned" && g.assignmentId ? (
        <button type="button" className={css.acceptBtn} onClick={(e) => { e.stopPropagation(); void api(`/api/assignments/${g.assignmentId}`, { method: "PUT", body: { status: "accepted" } }).then(onMkSaved).catch(() => {}); }}>✓ Принять игру</button>
      ) : null}
      {canReport && onReport ? (
        <button
          type="button"
          className={css.reportBtn}
          onClick={(e) => { e.stopPropagation(); onReport(g); }}
        >
          Сдать отчёт ›
        </button>
      ) : null}
      {open ? (
        <div className={css.gameExpand}>
          {g.client && g.client !== "[encrypted]" ? (
            <div className={css.gxRow}><span className={css.lbl}>Клиент</span><span className={css.val}>{g.client}</span></div>
          ) : null}
          {g.comment ? (
            <div className={css.gxRow}><span className={css.lbl}>Комментарий</span><span className={css.val}>{g.comment}</span></div>
          ) : null}
          {g.teaRoom ? (
            <div className={css.gxRow}><span className={css.lbl}>Чайная</span><span className={css.val}>{g.teaRoom}</span></div>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}
